import { useCallback, useSyncExternalStore } from "react";

/**
 * Какие микрофон и камеру брать в звонок по умолчанию. Живёт на устройстве
 * (`deviceId` у каждого браузера свой), поэтому localStorage, а не профиль.
 * Читает `LiveKitClient` при создании комнаты, пишет строка «Звонок» профиля.
 */
export type PreferredDeviceKind = "audioinput" | "videoinput" | "audiooutput";
export type PreferredDevices = Partial<Record<PreferredDeviceKind, string>>;

export const PREFERRED_DEVICES_STORAGE_KEY = "pbx.media-devices.v1";

// audiooutput (динамики) — только там, где браузер умеет setSinkId
// (Chromium/Electron); LiveKit применяет его ко всем входящим аудио.
const KINDS: readonly PreferredDeviceKind[] = [
  "audioinput",
  "videoinput",
  "audiooutput",
];
const listeners = new Set<() => void>();
const notify = (): void => listeners.forEach((listener) => listener());

const read = (): PreferredDevices => {
  try {
    const raw = window.localStorage.getItem(PREFERRED_DEVICES_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const out: PreferredDevices = {};
    for (const kind of KINDS) {
      const value = (parsed as Record<string, unknown>)[kind];
      if (typeof value === "string" && value) out[kind] = value;
    }
    return out;
  } catch {
    return {};
  }
};

const write = (next: PreferredDevices): void => {
  try {
    if (Object.keys(next).length === 0) {
      window.localStorage.removeItem(PREFERRED_DEVICES_STORAGE_KEY);
    } else {
      window.localStorage.setItem(
        PREFERRED_DEVICES_STORAGE_KEY,
        JSON.stringify(next),
      );
    }
  } catch {
    // QuotaExceeded / private mode — выбор проживёт до перезагрузки.
  }
};

export const preferredDevices = {
  read,
  get(kind: PreferredDeviceKind): string | null {
    return read()[kind] ?? null;
  },
  set(kind: PreferredDeviceKind, deviceId: string | null): void {
    const next: PreferredDevices = { ...read() };
    if (deviceId) next[kind] = deviceId;
    else delete next[kind];
    write(next);
    notify();
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  clear(): void {
    write({});
    notify();
  },
};

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === null || event.key === PREFERRED_DEVICES_STORAGE_KEY) {
      notify();
    }
  });
}

/** Реактивный выбор устройства одного вида. */
export const usePreferredDevice = (
  kind: PreferredDeviceKind,
): [string | null, (deviceId: string | null) => void] => {
  const deviceId = useSyncExternalStore(
    preferredDevices.subscribe,
    () => preferredDevices.get(kind),
    () => null,
  );
  const setDeviceId = useCallback(
    (next: string | null) => preferredDevices.set(kind, next),
    [kind],
  );
  return [deviceId, setDeviceId];
};
